import { cartService } from '../services/api';

const BAG_KEY = 'sheenwear_bag';

/** Filled by BagProvider so login() can merge even before localStorage persist runs. */
export const guestBagReactSnapshotRef = { current: [] };

/** Single-flight: concurrent callers await the same merge. */
let mergeInFlight = null;

function parseGuestItemsFromStorage() {
  const raw = localStorage.getItem(BAG_KEY);
  if (!raw) return [];
  try {
    const guestItems = JSON.parse(raw);
    return Array.isArray(guestItems) ? guestItems : [];
  } catch {
    return [];
  }
}

function mergeGuestLinesByItemId(a, b) {
  const map = new Map();
  for (const item of [...a, ...b]) {
    if (!item || !item.itemId) continue;
    const prev = map.get(item.itemId);
    const q = Math.max(1, Number(item.quantity) || 1);
    if (prev) {
      prev.quantity += q;
    } else {
      map.set(item.itemId, { ...item, quantity: q });
    }
  }
  return [...map.values()];
}

/**
 * Union in-memory snapshot + localStorage so login-merge never misses lines
 * (e.g. add-to-bag in the same tick as submit, before persist effect runs).
 */
function resolveGuestItems(getSnapshot) {
  const fromLs = parseGuestItemsFromStorage();
  let fromSnap = [];
  if (typeof getSnapshot === 'function') {
    try {
      const snap = getSnapshot();
      if (Array.isArray(snap) && snap.length > 0) fromSnap = snap;
    } catch (_) {
      /* ignore */
    }
  }
  if (fromLs.length === 0 && fromSnap.length === 0) return null;
  if (fromSnap.length === 0) return fromLs;
  if (fromLs.length === 0) return fromSnap;
  return mergeGuestLinesByItemId(fromLs, fromSnap);
}

/** Login sets React state before some effects run; cart POST needs Bearer token. */
async function waitForAuthToken(maxAttempts = 24, delayMs = 30) {
  for (let i = 0; i < maxAttempts; i++) {
    if (localStorage.getItem('authToken')) return;
    await new Promise((r) => setTimeout(r, delayMs));
  }
}

function normalizeProductId(item) {
  const raw = item._id ?? item.id;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object' && raw !== null && typeof raw.toString === 'function') {
    return String(raw.toString());
  }
  return String(raw);
}

function cartPayloadFromAddResponse(response) {
  if (!response || response.success === false) return null;
  return response.data ?? response.cart ?? null;
}

/**
 * POSTs each guest bag line to the authenticated user's cart.
 * Clears guest storage only after at least one successful add (per backend `success`).
 *
 * @param {() => void} clearGuestState
 * @param {() => unknown[] | null | undefined} [getSnapshot] latest guest lines from React (optional)
 */
export function mergeGuestCartToApi(clearGuestState, getSnapshot) {
  if (mergeInFlight) {
    return mergeInFlight;
  }

  mergeInFlight = (async () => {
    try {
      await waitForAuthToken();

      const guestItems = resolveGuestItems(getSnapshot);
      if (!guestItems?.length) return null;

      let lastCartResponse = null;
      let anySuccess = false;

      for (const item of guestItems) {
        const productId = normalizeProductId(item);
        if (!productId) continue;

        try {
          const response = await cartService.addToCart(
            productId,
            item.quantity || 1,
            item.selectedColor || 'default',
            item.selectedSize || 'default'
          );

          const failed = response && response.success === false;
          if (response && !failed) {
            anySuccess = true;
            const payload = cartPayloadFromAddResponse(response);
            if (payload) lastCartResponse = payload;
          }
        } catch (itemError) {
          console.warn('Guest cart merge line skipped:', itemError?.message || itemError);
        }
      }

      if (anySuccess && typeof clearGuestState === 'function') {
        clearGuestState();
        window.dispatchEvent(new Event('cartUpdated'));
      }

      return lastCartResponse;
    } finally {
      mergeInFlight = null;
    }
  })();

  return mergeInFlight;
}
